import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Globe, Lock, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/CategoryBadge";

const RoleSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="w-32 h-9" aria-label="Role" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="editor">Editor</SelectItem><SelectItem value="viewer">Viewer</SelectItem></SelectContent>
  </Select>
);

function PublicCard({ universe }) {
  const invalidate = useInvalidate();
  const link = `${window.location.origin}/p/${universe.id}`;
  const toggle = async (v) => {
    await api.patch(`/universes/${universe.id}`, { is_public: v });
    invalidate(["universe", universe.id]);
    toast.success(v ? "Universe is now public" : "Universe is private again");
  };
  return (
    <section className="rounded-md border border-[#2D3442] bg-[#13171F] p-6" data-testid="public-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-display text-2xl">{universe.is_public ? <Globe className="h-5 w-5 text-[#10B981]" /> : <Lock className="h-5 w-5 text-[#A8A090]" />}Public showcase</div>
          <p className="mt-1 text-sm text-[#A8A090] max-w-lg">Anyone with the link can explore the Lore Bible, maps and timelines. Editing and the AI scribe stay off.</p>
        </div>
        <Switch checked={!!universe.is_public} onCheckedChange={toggle} data-testid="public-toggle" />
      </div>
      {universe.is_public && (
        <div className="mt-4 flex gap-2">
          <Input readOnly value={link} className="font-mono-lf text-xs" data-testid="public-link-input" />
          <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} data-testid="copy-public-link-button"><Copy className="h-4 w-4" /></Button>
        </div>
      )}
    </section>
  );
}

function InviteForm({ uid, onDone }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/universes/${uid}/invites`, { email, role });
      toast.success(`Invitation saved for ${email}`, { description: "They'll see it when they sign in." });
      setEmail("");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap gap-2" data-testid="invite-form">
      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="co-author@example.com" className="flex-1 min-w-[220px]" data-testid="invite-email-input" />
      <RoleSelect value={role} onChange={setRole} testid="invite-role-select" />
      <Button type="submit" data-testid="invite-submit-button"><UserPlus className="h-4 w-4 mr-1.5" />Invite</Button>
    </form>
  );
}

export default function TeamPage() {
  const { universe } = useOutletContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const isOwner = universe.role === "owner";
  const uid = universe.id;
  const { data: members = [] } = useQuery({ queryKey: ["members", uid], queryFn: () => api.get(`/universes/${uid}/members`).then((r) => r.data) });
  const { data: invites = [] } = useQuery({ queryKey: ["invites", uid], queryFn: () => api.get(`/universes/${uid}/invites`).then((r) => r.data), enabled: isOwner });

  const act = async (fn, msg) => {
    try {
      await fn();
      invalidate(["members", uid], ["invites", uid]);
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const leave = () => act(async () => { await api.delete(`/universes/${uid}/members/${user.id}`); navigate("/"); }, "You left the universe");

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-10 max-w-4xl">
      <PageHeader eyebrow="Collaboration" title="Team & sharing" />
      <div className="space-y-8">
        {isOwner && <PublicCard universe={universe} />}
        {isOwner && (
          <section>
            <div className="lf-label mb-3">Invite a collaborator</div>
            <InviteForm uid={uid} onDone={() => invalidate(["invites", uid])} />
            {invites.length > 0 && (
              <div className="mt-4 divide-y divide-[#2D3442] rounded-md border border-[#2D3442]" data-testid="pending-invites">
                {invites.map((i) => (
                  <div key={i.id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`pending-invite-${i.id}`}>
                    <span className="text-[#E8DFD1]">{i.email} <span className="text-[#A8A090]">· {i.role} · pending</span></span>
                    <Button size="sm" variant="ghost" onClick={() => act(() => api.delete(`/invites/${i.id}`), "Invite revoked")} data-testid={`revoke-invite-${i.id}`}><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
        <section>
          <div className="lf-label mb-3">Members · {members.length}</div>
          <div className="divide-y divide-[#2D3442] rounded-md border border-[#2D3442] bg-[#13171F]" data-testid="member-list">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`member-row-${m.user_id}`}>
                <div>
                  <div className="text-[#F3EBDD]">{m.name}{m.user_id === user.id && <span className="text-[#A8A090]"> (you)</span>}</div>
                  <div className="text-xs text-[#A8A090]">{m.email}</div>
                </div>
                {isOwner && m.role !== "owner" ? (
                  <div className="flex items-center gap-2">
                    <RoleSelect value={m.role} onChange={(r) => act(() => api.patch(`/universes/${uid}/members/${m.user_id}`, { role: r }), "Role updated")} testid={`member-role-${m.user_id}`} />
                    <Button size="icon" variant="ghost" onClick={() => act(() => api.delete(`/universes/${uid}/members/${m.user_id}`), "Member removed")} data-testid={`remove-member-${m.user_id}`}><X className="h-4 w-4 text-red-400" /></Button>
                  </div>
                ) : (
                  <span className="lf-label">{m.role}</span>
                )}
              </div>
            ))}
          </div>
          {!isOwner && <Button variant="ghost" className="mt-4 text-red-400" onClick={leave} data-testid="leave-universe-button">Leave this universe</Button>}
        </section>
      </div>
    </div>
  );
}
